import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText,
  Trophy,
  Search,
  Eye,
  Building2,
  User,
  Calendar,
  Clock,
  CheckCircle2,
  Loader2,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FinalReportsListPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/final-reports`);
      setReports(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des rapports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadAttachment = async (reportId, att) => {
    try {
      const res = await axios.get(`${API}/final-reports/${reportId}/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'rapport.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Impossible de télécharger le fichier'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const filteredReports = reports.filter(report => {
    const search = searchTerm.toLowerCase();
    return (
      report.consultant_name?.toLowerCase().includes(search) ||
      report.sme_company_name?.toLowerCase().includes(search) ||
      report.project_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-emerald-600" />
            Rapports Finaux
          </h1>
          <p className="text-slate-500 mt-1">
            Liste des missions complétées avec rapport final soumis
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{reports.length}</p>
                  <p className="text-sm text-slate-500">Missions terminées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reports.reduce((sum, r) => sum + (r.total_hours || 0), 0).toFixed(0)}h
                  </p>
                  <p className="text-sm text-slate-500">Heures totales</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reports.reduce((sum, r) => sum + (r.validated_interventions || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Interventions validées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Set(reports.map(r => r.sme_company_id)).size}
                  </p>
                  <p className="text-sm text-slate-500">Entreprises accompagnées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Liste des rapports
            </CardTitle>
            <CardDescription>
              {filteredReports.length} rapport(s) trouvé(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Rechercher par consultant, entreprise ou projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-final-reports"
              />
            </div>

            {/* Table */}
            {filteredReports.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>Aucun rapport final soumis</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Consultant</TableHead>
                      <TableHead>Entreprise</TableHead>
                      <TableHead>Projet</TableHead>
                      <TableHead className="text-center">Heures</TableHead>
                      <TableHead className="text-center">Interventions</TableHead>
                      <TableHead>Date soumission</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{report.consultant_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {report.sme_company_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                            {report.project_name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">
                            {report.total_hours}h
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {report.validated_interventions}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-slate-500 text-sm">
                            <Calendar className="w-3 h-3" />
                            {formatDate(report.submitted_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {(report.attachments || []).map((att, i) => (
                              <Button
                                key={att.id}
                                variant="outline"
                                size="sm"
                                onClick={() => downloadAttachment(report.id, att)}
                                data-testid={`download-report-${report.id}-${i}`}
                                title={att.filename}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                {report.attachments.length > 1 ? `Fichier ${i + 1}` : 'PDF'}
                              </Button>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/final-report/${report.assignment_id}`)}
                              data-testid={`view-report-${report.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Consulter
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
